import mimeTypes from 'mime-types';

import { getContentType } from 'components/common/UploadModal/utils/getContentType';
import { MEDIAS_API_RESTORE, MEDIAS_API_UPLOAD, MEDIAS_API_ZIP } from 'constants/ApiConstants';
import { TRANSFORMATION_TYPES, contentTypeToMediaType } from 'constants/MediaConstants';

import { MediaStatusEnum } from 'modules/global/typing/enums/media.enum';
import { TEMPLATE_EXTENSIONS } from 'modules/medias/utils';

import * as actionTypes from './actionTypes';

/**
 * @param {import("./types/actions").GenerateZipPayload} data
 * @param {Object} [meta={}]
 * @returns {import("./types/actions").GenerateZipAction}
 */
export const generateZip = (data, meta = {}) => ({
  type: actionTypes.GENERATE_ZIP,
  payload: {
    request: {
      method: 'POST',
      url: MEDIAS_API_ZIP,
      data,
    },
  },
  meta,
});

/**
 * @param {{ zipId: string } & import("./types/actions").GenerateZipPayload} payload
 * @param {Object} [meta={}]
 * @returns {import("./types/actions").GenerateZipActionSuccess}
 */
export const generateZipSuccess = (payload, meta) => ({
  type: actionTypes.GENERATE_ZIP_SUCCESS,
  payload,
  meta,
});

export const getZip = (baseUrl, zipId, params) => ({
  type: actionTypes.GET_ZIP,
  payload: {
    request: {
      method: 'GET',
      url: `${baseUrl}/${zipId}`,
      params,
    },
  },
});

export const getZipSuccess = url => ({
  type: actionTypes.GET_ZIP_SUCCESS,
  payload: {
    url
  },
});

export const getZipFailure = message => ({
  type: actionTypes.GET_ZIP_FAILURE,
  payload: message,
  error: true,
});

export const uploadInTwoSteps = (
  { url = MEDIAS_API_UPLOAD, file, params = {} },
  { fetchThumbnail = true, ...meta }
) => {
  const contentType = getContentType({ file });

  return {
    type: actionTypes.UPLOAD_TWO_STEPS,
    payload: {
      request: {
        url,
        method: 'GET',
        params: {
          filename: file.name,
          contentType,
          ...params,
          type: params.type || contentTypeToMediaType(contentType),
        },
      },
      file,
      params,
    },
    meta: {
      fetchThumbnail,
      ...meta,
    },
  };
};

export const uploadInTwoStepsSuccess = (media, meta = {}) => ({
  type: actionTypes.UPLOAD_TWO_STEPS_SUCCESS,
  payload: media,
  meta,
});

export const uploadInTwoStepsFailure = (message, meta = {}) => ({
  type: actionTypes.UPLOAD_TWO_STEPS_FAILURE,
  payload: message,
  error: true,
  meta,
});

const applyTransformation = ({ mediaId, transformations, baseUrl }, meta = {}) => ({
  type: actionTypes.APPLY_TRANSFORMATIONS,
  payload: {
    request: {
      url: `${baseUrl}/${mediaId}/transform`,
      method: 'POST',
      data: { transformations },
    },
  },
  meta: {
    ...meta,
    mediaId,
    transformations,
  },
});

export const applyTransformationSuccess = (media, meta = {}) => ({
  type: actionTypes.APPLY_TRANSFORMATIONS_SUCCESS,
  payload: { media },
  meta,
});

export const applyTransformationFailure = message => ({
  type: actionTypes.APPLY_TRANSFORMATIONS_FAILURE,
  payload: message,
  error: true,
});

export const generateClippingTransformation = (mediaId, baseUrl, meta = {}) =>
  applyTransformation({ mediaId, transformations: [TRANSFORMATION_TYPES.CLIPPING], baseUrl }, meta);

export const clearUploadedMedias = source => ({
  type: actionTypes.CLEAR_UPLOADED_MEDIAS,
  payload: { source },
});

export const flushUploadedMedias = (source, mediaId) => ({
  type: actionTypes.FLUSH_UPLOADED_MEDIAS,
  payload: { source, mediaId },
});

export const deleteMedia = (mediaId, meta = {}) => ({
  type: actionTypes.DELETE_MEDIA,
  payload: {
    request: {
      url: `${MEDIAS_API_UPLOAD}/${mediaId}`,
      method: 'DELETE',
    },
    mediaId,
  },
  meta,
});

export const deleteMediaSuccess = (mediaId, meta = {}) => ({
  type: actionTypes.DELETE_MEDIA_SUCCESS,
  payload: { mediaId },
  meta,
});

/**
 * @typedef {{
 *  baseUrl?: string,
 *  linkURL?: string,
 *  medias: import('./types/actions').MediaLibraryItem[],
 *  uploadParams?: Record<string, unknown>,
 *  additionalData?: Object,
 *  temporary?: boolean,
 *  persistAtUpload?: boolean,
 *  params?: Record<string, unknown>,
 * }} uploadMediasParams
 */

/**
 * @param {uploadMediasParams} params
 * @param {Object=} [meta]
 * @returns {import('./types/actions').UploadMediasAction}
 */
// eslint-disable-next-line max-len
export const uploadMedias = ({ baseUrl, linkURL, medias, uploadParams, uploadData, additionalData, persistAtUpload, temporary = false, params = {} }, meta = {}) => ({
  type: actionTypes.UPLOAD_MEDIAS,
  payload: {
    medias,
    params,
    baseUrl,
    linkURL,
    temporary,
    uploadData,
    uploadParams,
    additionalData,
    persistAtUpload,
  },
  meta,
});

/**
 * @param {import('./types/actions').UploadMediasSuccessOrFailurePayload} medias
 * @param {Object=} [meta]
 * @returns {import('./types/actions').UploadMediasSuccessAction}
 */
export const uploadMediasSuccess = (medias, meta = {}) => ({
  type: actionTypes.UPLOAD_MEDIAS_SUCCESS,
  payload: medias,
  meta,
});

/**
 * @param {import('./types/actions').UploadMediasSuccessOrFailurePayload} medias
 * @param {Object=} [meta]
 * @returns {import('./types/actions').UploadMediasFailureAction}
 */
export const uploadMediasFailure = (medias, meta = {}) => ({
  type: actionTypes.UPLOAD_MEDIAS_FAILURE,
  payload: medias,
  error: true,
  meta,
});

/**
 * Fetch a file from its URL and download it.
 * - Please use the simple downloadURL function in first,
 * - This function is useful when the server return the header `Content-Disposition` with a defined filename and we absolutely want given an another filename
 * - ⚠ The download process will start only when the file was completely fetched.
 * @param {string} url
 * @param {string?} fileName
 * @param {Object=} [meta]
 */
export const downloadFetchedURL = (url, fileName, meta = {}) => ({
  type: actionTypes.DOWNLOAD_MEDIA,
  payload: {
    url,
    fileName
  },
  meta,
});

export const downloadFetchedURLSuccess = (meta = {}) => ({
  type: actionTypes.DOWNLOAD_MEDIA_SUCCESS,
  meta
});

export const downloadFetchedURLFailure = (error, meta = {}) => ({
  type: actionTypes.DOWNLOAD_MEDIA_FAILURE,
  payload: error,
  error: true,
  meta
});

export const persistItemsToEntity = ({ url, data, params }, meta = {}) => ({
  type: actionTypes.PERSIST_ITEMS_TO_ENTITY,
  payload: {
    request: {
      method: 'POST',
      url,
      data,
      params,
    },
  },
  meta,
});

export const patchMedia = ({ data, params }, meta = {}) => ({
  type: actionTypes.PATCH_MEDIA,
  payload: {
    request: {
      url: `${MEDIAS_API_UPLOAD}/${data.id}`,
      method: 'PATCH',
      data,
      params,
    },
  },
  meta,
});

export const getTemplate = ({ url, params }, meta) => ({
  type: actionTypes.GET_TEMPLATE,
  payload: {
    request: {
      method: 'GET',
      url,
      responseType: params.extension === TEMPLATE_EXTENSIONS.XLSX ? 'arraybuffer' : 'text',
      headers: { accept: mimeTypes.lookup(params.extension) },
      params,
    },
    params
  },
  meta
});

export const getTemplateSuccess = (template, meta = {}) => ({
  type: actionTypes.GET_TEMPLATE_SUCCESS,
  payload: template,
  meta
});

export const getTemplateFailure = (error, meta = {}) => ({
  type: actionTypes.GET_TEMPLATE_FAILURE,
  payload: error,
  error: true,
  meta
});

export const restoreAllMedias = (entityType, id, meta = {}) => ({
  type: actionTypes.DEFROST_MEDIAS_REQUEST,
  payload: {
    request: {
      method: 'PUT',
      url: `${MEDIAS_API_RESTORE}/${entityType}/entity/${id}`,
    },
  },
  meta: {
    ...meta,
  },
});

export const defrostMediaSuccess = (mediaIds, meta = {}) => ({
  type: actionTypes.DEFROST_MEDIAS_SUCCESS,
  payload: mediaIds.map(mediaId => ({ id: mediaId, status: MediaStatusEnum.RESTORING })),
  meta
});
